import { MutableRefObject, useEffect, useMemo, useRef } from 'react';

import {
  GridApi,
  GridAutosizeOptions,
  GridColumnVisibilityModel,
  GridValidRowModel,
} from '@mui/x-data-grid-premium';

import { GeneratikTableColumn } from '../GeneratikTable.types';
import useGeneratikTableStore, { buildColumnWidthKey } from '../generatikTableStore';
import { isPeriodField } from '../timeline/timeline.utils';

export interface UseAutosizeColumnsOnMountParams<R extends GridValidRowModel> {
  autosizeOnMount: boolean;
  rows: R[];
  columns: GeneratikTableColumn<R>[];
  apiRef: MutableRefObject<GridApi>;
  autosizeOptions?: GridAutosizeOptions;
  headersRequest?: { pageType: string; resourceId?: string };
  columnVisibilityModel?: GridColumnVisibilityModel;
}

const useAutosizeColumnsOnMount = <R extends GridValidRowModel>({
  autosizeOnMount,
  rows,
  columns,
  apiRef,
  autosizeOptions,
  headersRequest,
  columnVisibilityModel,
}: UseAutosizeColumnsOnMountParams<R>): void => {
  const autosizeWidthKey = headersRequest
    ? buildColumnWidthKey(headersRequest.pageType, headersRequest.resourceId)
    : null;
  const hasPersistedWidths = useGeneratikTableStore((state) => {
    if (!autosizeWidthKey) return false;
    const widths = state.columnWidths[autosizeWidthKey];
    return !!widths && Object.keys(widths).length > 0;
  });

  // Re-autosizing when columns are hidden/shown lets the remaining columns
  // re-measure and expand to cover the freed width. Tables with persisted
  // widths skip autosize entirely; their flex-based widths refill on their own.
  const hiddenColumnsSignature = useMemo(
    () =>
      Object.entries(columnVisibilityModel ?? {})
        .filter(([, visible]) => visible === false)
        .map(([field]) => field)
        .sort()
        .join('|'),
    [columnVisibilityModel],
  );

  // Callers rebuild their `columns` array on every render whose deps happen to include
  // it (e.g. a column's renderHeader closing over a callback that isn't reference-stable
  // across an unrelated data refetch) — that gives a fresh array/object identity with the
  // exact same field set. Depending on `columns` itself re-triggered autosizeColumns() on
  // every such render, which visibly snapped column widths over to their measured content
  // size and back. Key the effect off what autosize actually cares about (which fields
  // exist and their fixed-width bounds) instead, and read the latest columns via a ref so
  // the effect body still sees current data without re-running when only identity changed.
  const columnsRef = useRef(columns);
  columnsRef.current = columns;

  const columnsSignature = useMemo(
    () =>
      columns
        .map(
          (col) =>
            `${col.field}:${col.resizable === false ? 1 : 0}:${col.minWidth ?? ''}:${col.maxWidth ?? ''}`,
        )
        .join('|'),
    [columns],
  );

  useEffect(() => {
    if (!autosizeOnMount || rows.length === 0 || !apiRef.current) return;
    if (typeof apiRef.current.getAllColumns !== 'function') return;
    if (hasPersistedWidths) return;

    const fixedWidthFields = new Set(
      columnsRef.current
        .filter(
          (col) =>
            col.resizable === false &&
            col.minWidth !== undefined &&
            col.maxWidth !== undefined &&
            col.minWidth === col.maxWidth,
        )
        .map((col) => col.field),
    );

    const autosizableFields = (
      autosizeOptions?.columns ??
      apiRef.current
        .getAllColumns()
        .map((col) => col.field)
        .filter((field) => !fixedWidthFields.has(field))
    ).filter((field) => !isPeriodField(field));

    const timeoutId = setTimeout(() => {
      if (typeof apiRef.current?.autosizeColumns !== 'function') return;
      apiRef.current.autosizeColumns({
        includeHeaders: true,
        includeOutliers: false,
        expand: true,
        ...autosizeOptions,
        columns: autosizableFields,
      });
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [
    autosizeOnMount,
    rows.length,
    apiRef,
    autosizeOptions,
    hasPersistedWidths,
    columnsSignature,
    hiddenColumnsSignature,
  ]);
};

export default useAutosizeColumnsOnMount;
